/**
 * One Journey step in the cofactor.md view. Read-only today, but deliberately
 * structured as an identity-bound block: it owns `data-node-id={nodeId}`, a
 * left gutter for an optional status indicator, and a node-type badge. That
 * makes the future seams drop-in:
 *   - live run status → pass `status` (a dot/spinner renders in the gutter);
 *   - active step highlight → pass `isCurrent`;
 *   - inline editing → swap the rendered body for an editor bound to nodeId.
 *
 * @module FlowDesigner/spec/SpecStepBlock
 */

import React from 'react';
import { Markdown, TagBadge, type TagColor } from '@chatsdet/ui';
import { CheckCircle, Warning, Info } from '@phosphor-icons/react';
import type { SpecAssertion } from '@chatsdet/canary-spec/browser';

export type StepStatus = 'running' | 'passed' | 'failed' | 'skipped';

const NODE_TYPE_COLOR: Record<string, TagColor> = {
  login: 'purple',
  navigate: 'blue',
  action: 'teal',
  assertion: 'green',
  condition: 'orange',
  seed: 'indigo',
  setup: 'indigo',
  wait: 'gray',
  api_sequence: 'yellow',
  sandbox_action: 'pink',
  notification: 'pink',
  audio: 'purple',
  'audio-assertion': 'purple',
  visual_checkpoint: 'purple',
  end: 'gray',
};

const STATUS_DOT: Record<StepStatus, string> = {
  running: 'bg-blue-500 animate-pulse',
  passed: 'bg-green-500',
  failed: 'bg-red-500',
  skipped: 'bg-gray-400',
};

/** Title is monospaced for node types whose primary value is a literal token. */
const MONO_TITLE = new Set(['navigate', 'seed', 'setup', 'sandbox_action']);

/** Severity → a single colored icon (the color carries the severity). */
function assertionStyle(severity: string): {
  Icon: React.ComponentType<{ size?: number; weight?: 'fill' | 'regular'; className?: string }>;
  iconCls: string;
} {
  const sev = severity.toLowerCase();
  if (sev === 'fail' || sev === 'fatal' || sev === 'error') {
    return { Icon: CheckCircle, iconCls: 'text-red-500' };
  }
  if (sev === 'warn' || sev === 'warning') {
    return { Icon: Warning, iconCls: 'text-amber-500' };
  }
  return { Icon: Info, iconCls: 'text-muted-foreground' };
}

const AssertionRow: React.FC<{ assertion: SpecAssertion }> = ({ assertion }) => {
  const { Icon, iconCls } = assertionStyle(assertion.severity);
  return (
    <div className="flex items-start gap-1.5 text-sm leading-snug">
      <Icon size={15} weight="fill" className={`mt-0.5 shrink-0 ${iconCls}`} />
      <span className="min-w-0 flex-1 text-foreground/90">{assertion.condition}</span>
    </div>
  );
};

export interface SpecStepBlockProps {
  index: number;
  nodeId: string;
  nodeType: string;
  title: string;
  description?: string;
  assertions: SpecAssertion[];
  details: string[];
  /** Live run status for this step (optional — read-only view passes none). */
  status?: StepStatus;
  /** Whether this step is the one currently executing. */
  isCurrent?: boolean;
}

export const SpecStepBlock: React.FC<SpecStepBlockProps> = ({
  index,
  nodeId,
  nodeType,
  title,
  description,
  assertions,
  details,
  status,
  isCurrent,
}) => {
  const color = NODE_TYPE_COLOR[nodeType] ?? 'gray';
  const detailMarkdown = details.length > 0 ? details.map((d) => `- ${d}`).join('\n') : null;
  return (
    <div
      data-node-id={nodeId}
      data-node-type={nodeType}
      className={[
        'group/step relative flex gap-3 rounded-lg border px-4 py-3 transition-colors',
        isCurrent
          ? 'border-primary/60 bg-primary/5'
          : 'border-border/60 bg-card/40 hover:border-border',
      ].join(' ')}
    >
      {/* Gutter: step number + status seam */}
      <div className="flex w-8 shrink-0 flex-col items-center pt-0.5">
        <span className="text-xs font-semibold tabular-nums text-muted-foreground">{index}</span>
        {status && (
          <span
            className={`mt-1.5 h-2 w-2 rounded-full ${STATUS_DOT[status]}`}
            aria-label={`status: ${status}`}
          />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {/* Pill + clean title */}
        <div className="flex items-center gap-2">
          <TagBadge name={nodeType} color={color} size="sm" />
          {title && (
            <span
              className={
                MONO_TITLE.has(nodeType)
                  ? 'truncate font-mono text-[13px] text-foreground'
                  : 'font-medium text-foreground'
              }
            >
              {title}
            </span>
          )}
        </div>

        {/* Description note (softer than a raw blockquote) */}
        {description && (
          <p className="rounded-md bg-muted/40 px-3 py-2 text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        )}

        {/* Authored detail lines (fields, versions, flags) */}
        {detailMarkdown && (
          <Markdown compact className="text-sm">
            {detailMarkdown}
          </Markdown>
        )}

        {/* Visualized assertions */}
        {assertions.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {assertions.map((a, i) => (
              <AssertionRow key={i} assertion={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
